import { useMutation } from '@apollo/client';
import { Form, PillGroup, TextBox } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  reportBookingComplete,
  setAppointmentId,
  setCurrentCarouselItem,
  setCurrentCarouselItemToPrevious,
  setEmail,
  setNotificationPreference,
  setSelectedTimeSlot,
} from 'actions';
import { CarouselContinueButton } from 'components/CarouselContinueButton';
import { Phrase } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { ReactComponent as InputEmailSvg } from 'components/svg/InputEmail.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselBottomButtonType,
  CarouselReduxReducer,
  IState,
  NotificationPreference,
  ServiceType,
} from 'typings';
import {
  ICustomerInput,
  ISubmitBookingRequestMutation,
  ISubmitBookingRequestMutationVariables,
} from 'typings/GraphQL';
import { buildBookingOptions } from 'utils/buildBookingOptions';
import { EMAIL_REGEXP } from 'utils/emailRegexp';

import {
  Container,
  EmailContainer,
  NotificationPreferenceContainer,
  PillContainer,
} from './styles';

const submitBookingRequestMutation = loader(
  '../../queries/submitBookingRequest.graphql',
);

export const CarouselItemSalesSummaryBottom = () => {
  const dispatch = useThunkDispatch();

  const {
    accountTracking,
    addressId = '',
    analytics,
    customerLocationPhone,
    customerPhone,
    customForms,
    firstName = '',
    flowId,
    email = '',
    erpKey,
    isEmergency,
    lastName = '',
    leadId,
    mobileNotificationOptIn,
    notificationPreference,
    preferredPartnerId,
    selectedEnergySources,
    selectedEquipment,
    selectedSchedulePreference,
    selectedSchedulePreferenceTime,
    selectedTimeSlot,
    serviceCodeId,
    serviceType = ServiceType.SALES,
    systemComments,
  } = useSelector((state: IState) => ({
    accountTracking: state.accountTracking,
    addressId: state.userInput.address.id,
    analytics: state.sourceTrackingAnalytics,
    currentItemId: state.serviceCarousel.currentItemId,
    customForms: state.userInput.customForms,
    customerLocationPhone: state.userInput.customerLocationPhone,
    customerPhone: state.userInput.customerPhone,
    email: state.userInput.email,
    erpKey: state.client.erpKey,
    firstName: state.userInput.firstName,
    flowId: state.client.flow?.id,
    isEmergency: state.userInput.isEmergency,
    lastName: state.userInput.lastName,
    leadId: state.userInput.leadId,
    mobileNotificationOptIn: state.userInput.mobileNotificationOptIn,
    // Make a copy of notificationPreference rather than passing the array itself. This is to avoid inadvertently
    // mutating state.userInput.notificationPreference when we mutate it in the render function
    notificationPreference: [...state.userInput.notificationPreference],
    preferredPartnerId: state.userInput.preferredPartner?.partnerId,
    selectedEnergySources: state.userInput.selectedEnergySources,
    selectedEquipment: state.userInput.selectedEquipment,
    selectedSchedulePreference: state.userInput.selectedSchedulePreference,
    selectedSchedulePreferenceTime:
      state.userInput.selectedSchedulePreferenceTime,
    selectedTimeSlot: state.userInput.selectedTimeSlot,
    serviceCodeId: state.userInput.serviceCodeId,
    serviceType: state.userInput.serviceType,
    systemComments: state.userInput.systemComments,
  }));

  const strings = useStringContext();
  const { enterpriseEnabled, enterpriseVersion } = useTheme();

  const [submitBookingRequest, { error, loading: isLoading }] = useMutation<
    ISubmitBookingRequestMutation,
    ISubmitBookingRequestMutationVariables
  >(submitBookingRequestMutation, {
    onCompleted: (result: ISubmitBookingRequestMutation) => {
      const { appointment = null, errorCode = null } =
        result.submitBookingRequest || {};

      if (errorCode && selectedTimeSlot) {
        dispatch(
          setSelectedTimeSlot({ ...selectedTimeSlot, isUnavailable: true }),
        );
      } else if (appointment) {
        dispatch(setAppointmentId(appointment.id));
        dispatch(
          setCurrentCarouselItem(
            CarouselReduxReducer.SERVICE,
            CarouselItemId.ConfirmFinishSales,
          ),
        );
        dispatch(reportBookingComplete());
      }
    },
  });

  const handleBackToCalendar = () => {
    dispatch(
      setCurrentCarouselItemToPrevious(
        CarouselReduxReducer.SERVICE,
        CarouselItemId.SchedulePreferenceSales,
      ),
    );
  };

  const handleEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch(setEmail(event.currentTarget.value));
  };

  const handleNotificationPreferenceChange = (
    _event: React.FormEvent<HTMLInputElement>,
    value?: string | Array<string>,
  ) => {
    if (Array.isArray(value)) {
      dispatch(
        setNotificationPreference(value as Array<NotificationPreference>),
      );
    }
  };

  const handleContinueClick = async () => {
    if (!selectedTimeSlot) {
      return;
    }

    if (selectedTimeSlot.isUnavailable) {
      handleBackToCalendar();

      return;
    }

    const phoneId = customerPhone.id ?? '';
    const phoneIdForNotification =
      [customerPhone, customerLocationPhone].find(({ isMobile }) => !!isMobile)
        ?.id ?? phoneId;

    const customer: ICustomerInput = {
      addressId,
      email,
      firstName,
      lastName,
      notificationPreference,
      phoneId,
      phoneIdForNotification,
    };

    const options = buildBookingOptions({
      accountTracking,
      analytics,
      customForms,
      enterpriseEnabled,
      enterpriseVersion,
      erpKey,
      flowId,
      isEmergency,
      leadId,
      mobileNotificationOptIn,
      preferredPartnerId,
      schedulePreferenceEnabled: true,
      selectedEnergySources,
      selectedEquipment,
      selectedSchedulePreference,
      selectedSchedulePreferenceTime,
      serviceCodeId,
      strings,
      systemComments,
    });

    submitBookingRequest({
      variables: {
        customer,
        options,
        serviceType,
        startTime: selectedTimeSlot.startTime,
      },
    });
  };

  const options = [
    {
      key: NotificationPreference.EMAIL,
      label: Phrase({ id: 'SALES_SUMMARY.EMAIL.BUTTON' }),
      value: NotificationPreference.EMAIL,
    },
    {
      key: NotificationPreference.SMS,
      label: Phrase({ id: 'SALES_SUMMARY.TEXT.BUTTON' }),
      value: NotificationPreference.SMS,
    },
  ];

  const isUnavailable = selectedTimeSlot?.isUnavailable ?? false;
  const isEmailChecked = notificationPreference.includes(
    NotificationPreference.EMAIL,
  );
  const isEmailValid = isEmailChecked ? EMAIL_REGEXP.test(email) : true;
  const isFormValid = isEmailValid && notificationPreference.length > 0;
  const errorMessage =
    error && !isLoading ? Phrase({ id: 'GENERIC_GRAPHQL_ERROR' }) : '';
  const continueButtonLabel = isUnavailable
    ? Phrase({ id: 'SALES_SUMMARY.BACK.LABEL' })
    : Phrase({ id: 'SALES_SUMMARY.CONTINUE.LABEL' });
  const continueButtonType = isUnavailable
    ? CarouselBottomButtonType.BACK
    : CarouselBottomButtonType.CONTINUE;

  return (
    <Form>
      <Container>
        <NotificationPreferenceContainer
          isEmailChecked={isEmailChecked}
          isUnavailable={isUnavailable}
        >
          <PillContainer>
            <PillGroup
              onChange={handleNotificationPreferenceChange}
              options={options}
              title={Phrase({ id: 'SALES_SUMMARY.LEGEND' })}
              value={[...notificationPreference]}
            />
          </PillContainer>
          <EmailContainer isEmailChecked={isEmailChecked}>
            <TextBox
              autoComplete="email"
              isSuccessful={isEmailValid && isEmailChecked}
              label={Phrase({ id: 'SALES_SUMMARY.EMAIL.LABEL' })}
              leftIcon={InputEmailSvg}
              name="email"
              onChange={handleEmailChange}
              type="email"
              value={email}
            />
          </EmailContainer>
        </NotificationPreferenceContainer>
        <CarouselContinueButton
          buttonType={continueButtonType}
          errorMessage={errorMessage}
          isDisabled={!isFormValid}
          isLoading={isLoading}
          label={continueButtonLabel}
          onClick={() => {
            if (!isLoading) {
              handleContinueClick();
            }
          }}
        />
      </Container>
    </Form>
  );
};
